import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  authService,
  partnerService,
  userService,
} from '../lib/api-services';
import { AuthUser, handleApiError } from '../lib/api-client';
import { CreateFormValues } from '@/components/VersionRule/CreateFormDailog';

type CreatePortalFormPayload = {
  data: CreateFormValues;
  formType: string;
};

type Togglepayload = {
  id: number;
  formType: string;
};

// ==================== AUTH HOOKS ====================

export function useAuthMe(options?: UseQueryOptions<AuthUser, Error>) {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authService.me,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: true,
    ...options,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authService.logout,
    onSuccess: () => {
      queryClient.clear();
      window.location.href = 'https://example.com/login';
    },
    onError: (error) => {
      console.error('Logout error:', handleApiError(error));
    },
  });
}

//===================Partners API===============
export function usePartnerData(pageNumber: number, pageSize?: number, partnerName?: string, partnerType?: string, partnerSubType?: string) {
  return useQuery({
    queryKey: ['partners', pageNumber, pageSize, partnerName, partnerType, partnerSubType],
    queryFn: () => partnerService.getPartnersData({
      pageNumber,
      pageSize,
      partnerName,
      partnerType,
      partnerSubType,
    }),
  });
}

// Audit log Get Hook
export function useAuditLog(filters?: any) {
  return useQuery({
    queryKey: ["auditLog", filters],
    queryFn: () => userService.getAuditLog(filters),
  });
}

// ==================== USER HOOKS ====================

export function usePortalForms(page: number = 1, pageSize: number = 10, formType: string, formCode?: number, formName?: string, partnerType?: string, offerId?: string | null) {
  console.log("offerId", offerId)
  return useQuery({
    queryKey: ['portals', page, pageSize, formCode, formName, formType, partnerType, offerId],
    queryFn: () => userService.getPortalForms({
      page,
      pageSize,
      formType,
      formCode,
      formName,
      partnerType,
      offerId
    }),
  });
}

export function useFilterForms(q: string, pageSize: number, formType: string) {
  return useQuery({
    queryKey: ['portals', q, pageSize, formType],
    queryFn: () => userService.getFilterForms(
      q,
      pageSize,
      formType
    ),
  });
}

export function getVersionForm(id: number, formType: string, pageIndex: number, pageSize: number) {
  return useQuery({
    queryKey: ['portalsVersion', id, formType, pageIndex, pageSize],
    queryFn: () => userService.versionForm(id, formType, pageIndex, pageSize),
  });
}

export function usetoggglePublishForm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, formType }: Togglepayload) => userService.togglePublishForm(id, formType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portals'] });
    },
    onError: (error) => {
      console.error('Create user error:', handleApiError(error));
    },
  });
}

export function useCreatePortalForm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, formType }: CreatePortalFormPayload) => userService.createPortalForm(data, formType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portals'] });
    },
    onError: (error) => {
      console.error('Create user error:', handleApiError(error));
    },
  });
}

export function useOfferReferenceForm() {
  return useMutation({
    mutationFn: ({ Id, referenceFormId }: { Id: number; referenceFormId: number }) =>
      authService.offerFormId(Id, referenceFormId)
  });
}

export function useAdditionalOfferReferenceForm() {
  return useMutation({
    mutationFn: ({ Id, additionalOfferId, referenceFormId }: { Id: number; additionalOfferId: number; referenceFormId: number }) =>
      authService.additionalOfferFormId(Id, additionalOfferId, referenceFormId)
  });
}

export function useUpdatePortalForm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ Id, data, formType }: { Id: number; data: CreateFormValues, formType: string }) =>
      userService.updateUser(Id, data, formType),
    onSuccess: (updatedUser) => {
      queryClient.invalidateQueries({ queryKey: ['portals'] });
      // queryClient.setQueryData(['users', updatedUser.id], updatedUser);
    },
    onError: (error) => {
      console.error('Update user error:', handleApiError(error));
    },
  });
}

export function useDeletePortalForm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ Id, formType, authCode }: { Id: number | undefined; formType: string; authCode?: string }) => userService.deletePortalForm(Id, formType, authCode),
    onMutate: async (Id) => {
      await queryClient.cancelQueries({ queryKey: ['portals'] });
      const previousForms = queryClient.getQueryData(['portals']);
      return { previousForms };
    },
    onError: (error, variables, context) => {
      if (context?.previousForms) {
        queryClient.setQueryData(['portals'], context.previousForms);
      }
      console.error('Delete user error:', handleApiError(error));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['portals'] });
    },
  });
}


// ==================== AI Console HOOKS ====================
export function useCreateFormAI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ prompt }: { prompt: string; }) =>
      userService.createFormAI(prompt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portals"] });
    },
  });
}
